import React, { ChangeEvent, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import {
  BulkCreateRoadmapItemsResponse,
  CreateRoadmapItemResponse,
  RoadmapDto,
  RoadmapItemDto,
  RoadmapItemSourceType,
} from "@lib/ShiOneClient";
import CloseIcon from "@mui/icons-material/Close";
import { useQueryClient } from "@tanstack/react-query";
import useGetRoadmapsByCustomerId from "shared-ui/src/features/roadmaps-feature/hooks/useGetRoadmapsByCustomerId";
import SendToDialogRoadmapsView from "@features/assessments-feature/components/priorities-recommendations/SendToDialogRoadmapsView";
import SendToDialogInitiativesView from "@features/assessments-feature/components/priorities-recommendations/SendToDialogInitiativesView";
import LoadingOrError from "shared-ui/src/global-layout/LoadingOrError";
import useCreateRoadmapItem from "shared-ui/src/features/roadmaps-feature/hooks/useCreateRoadmapItem";
import useBulkCreateRoadmapItems from "shared-ui/src/features/roadmaps-feature/hooks/useBulkCreateRoadmapItems";
import { initialRoadmapItemState } from "shared-ui/src/features/roadmaps-feature/utils/roadmapProps";
import { useOptionalAssessmentViewContext } from "@features/assessments-feature/contexts-providers/AssessmentViewContextProvider";
import { createRoadmapTaskFromRecommendation } from "shared-ui/src/features/roadmaps-feature/utils/createRoadmapTask";
import { SnackbarState } from "../../../../../../../../shared-ui/src/features/roadmaps-feature/interfaces/SnackbarState";
import SnackbarGroup from "shared-ui/src/features/roadmaps-feature/components/SnackbarGroup";

interface BuildRoadmapItemOptions {
  sourceId: string;
  capability: string;
  parentRoadmapItem: RoadmapItemDto;
  selectedRoadmap?: RoadmapDto | null;
  recommendations?: any[];
}

function buildRoadmapItem({
  sourceId,
  capability,
  parentRoadmapItem,
  selectedRoadmap,
  recommendations,
}: BuildRoadmapItemOptions): RoadmapItemDto {
  let recommendation = undefined;
  let sourceType = RoadmapItemSourceType.AssessmentRecommendation;

  if (recommendations) {
    recommendation = recommendations.find(
      (rec) => rec.recommendationId === sourceId,
    );
  }

  if (recommendation) {
    sourceType = recommendation.sourceType;
  }

  if (recommendation) {
    const context = {
      roadmapId: selectedRoadmap?.id ?? "",
      parentRoadmapItemId: parentRoadmapItem.roadmapItemId ?? "",
      initiativeTasks: [],
      index: 0,
    };
    const roadmapItem = createRoadmapTaskFromRecommendation({
      recommendation,
      context,
    });
    if (roadmapItem) {
      if (
        recommendation.sourceType ==
          RoadmapItemSourceType.AzureCloudRecommendation ||
        recommendation.sourceType ==
          RoadmapItemSourceType.AwsCloudRecommendation
      ) {
        return roadmapItem;
      }

      if (
        recommendation.sourceType ==
        RoadmapItemSourceType.AssessmentRecommendation
      ) {
        return RoadmapItemDto.fromJS({
          sourceId: roadmapItem.sourceId,
          sourceType: roadmapItem.sourceType,
          parentRoadmapItemId: roadmapItem.parentRoadmapItemId,
          roadmapId: roadmapItem.roadmapId,
          name: roadmapItem.name,
          recommendationId: recommendation?.recommendationId,
          startDate: roadmapItem.startDate,
          targetDate: roadmapItem.targetDate,
        });
      }
    }
  }

  const startDate = new Date();
  const targetDate = new Date();
  targetDate.setFullYear(targetDate.getFullYear() + 1);

  return RoadmapItemDto.fromJS({
    sourceId: sourceId,
    sourceType: sourceType,
    parentRoadmapItemId: parentRoadmapItem.roadmapItemId,
    roadmapId: selectedRoadmap?.id,
    name: capability,
    recommendationId: recommendation?.recommendationId,
    startDate,
    targetDate,
  });
}

const SendToDialog = ({
  isOpenSendToDialog,
  setIsOpenSendToDialog,
  selectedSourceIds: propSelectedSourceIds,
  setSelectedSourceIds: propSetSelectedSourceIds,
  invalidateQueryKeys,
  recommendations,
  onSuccessActions,
}: {
  isOpenSendToDialog: boolean;
  setIsOpenSendToDialog: (open: boolean) => void;
  selectedSourceIds?: Map<string, string>;
  setSelectedSourceIds?: (ids: Map<string, string>) => void;
  invalidateQueryKeys?: readonly unknown[][];
  recommendations?: any[];
  onSuccessActions?: (items?: any) => void;
}) => {
  const theme = useTheme();

  const context = useOptionalAssessmentViewContext();
  const selectedSourceIds = propSelectedSourceIds ?? context?.selectedSourceIds;
  const setSelectedSourceIds =
    propSetSelectedSourceIds ?? context?.setSelectedSourceIds;
  const BULK_CREATE_THRESHOLD = 10;

  const [snackbars, setSnackbars] = useState<SnackbarState[]>([]);
  const [selectedRoadmap, setSelectedRoadmap] = useState<RoadmapDto | null>(
    null,
  );
  const [selectedInitiative, setSelectedInitiative] =
    useState<RoadmapItemDto | null>(null);
  const [currentView, setCurrentView] = useState<
    "roadmapsView" | "initiativesView"
  >("roadmapsView");

  const { data: roadmaps, isLoading, isError } = useGetRoadmapsByCustomerId();

  const queryClient = useQueryClient();

  const { mutate: createNewRoadmapItem, isLoading: isCreating } =
    useCreateRoadmapItem(setSnackbars);

  const { mutate: bulkCreateNewRoadmapItems, isLoading: isBulkCreating } =
    useBulkCreateRoadmapItems();

  const handleRoadmapClick = (roadmap: RoadmapDto) => {
    setSelectedRoadmap(roadmap);
    setCurrentView("initiativesView");
  };

  const handleInitiativeClick = (initiative: RoadmapItemDto | null) => {
    selectedInitiative?.roadmapItemId === initiative?.roadmapItemId
      ? setSelectedInitiative(null)
      : setSelectedInitiative(initiative);
  };

  const handleChangeNewInitiativeName = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setSelectedInitiative({
      ...selectedInitiative,
      name: event.target.value,
    } as RoadmapItemDto);
  };

  const handleBackToRoadmaps = () => {
    setCurrentView("roadmapsView");
    setSelectedRoadmap(null);
    setSelectedInitiative(null);
  };

  const addErrorSnackbar = () => {
    setSnackbars((prev: SnackbarState[]) => [
      ...prev,
      {
        key: new Date().getTime() + Math.random(),
        message:
          "There was an issue creating the item(s), please try again later.",
        severity: "error",
      },
    ]);
  };

  const handleSubmitNewRoadmapItemFromAssessments = () => {
    const shouldUseBulkCreate =
      selectedSourceIds && selectedSourceIds.size > BULK_CREATE_THRESHOLD;

    const createTasksCallbackFunction = (parentRoadmapItem: RoadmapItemDto) => {
      if (shouldUseBulkCreate) {
        const bulkRoadmapItems: RoadmapItemDto[] = [];

        selectedSourceIds?.forEach((capability, sourceId) => {
          const newChildRoadmapItem = buildRoadmapItem({
            sourceId,
            capability,
            parentRoadmapItem,
            selectedRoadmap,
            ...(recommendations ? { recommendations } : {}),
          });

          bulkRoadmapItems.push(newChildRoadmapItem);
        });

        bulkCreateNewRoadmapItems(bulkRoadmapItems, {
          onSuccess: (response: BulkCreateRoadmapItemsResponse) => {
            setIsOpenSendToDialog(false);
            setSelectedSourceIds?.(new Map());

            if (invalidateQueryKeys && invalidateQueryKeys.length > 0) {
              invalidateQueryKeys.forEach((key) => {
                queryClient
                  .invalidateQueries({ queryKey: key, exact: false })
                  .then();
              });
            }
            if (onSuccessActions) {
              onSuccessActions();
            }
          },
          onError: () => {
            addErrorSnackbar();
          },
        });
      } else {
        selectedSourceIds?.forEach((capability, sourceId) => {
          const newChildRoadmapItem = buildRoadmapItem({
            sourceId,
            capability,
            parentRoadmapItem,
            selectedRoadmap,
            ...(recommendations ? { recommendations } : {}),
          });

          createNewRoadmapItem(newChildRoadmapItem, {
            onSuccess: (
              callbackFunctionResponse: CreateRoadmapItemResponse,
            ) => {
              if (callbackFunctionResponse.isSuccessful === true) {
                setIsOpenSendToDialog(false);
                setSelectedSourceIds?.(new Map());
                if (invalidateQueryKeys && invalidateQueryKeys.length > 0) {
                  invalidateQueryKeys.forEach((key) => {
                    queryClient
                      .invalidateQueries({ queryKey: key, exact: false })
                      .then();
                  });
                }

                if (onSuccessActions) {
                  onSuccessActions();
                }
              } else addErrorSnackbar();
            },
            onError: () => {
              addErrorSnackbar();
            },
          });
        });
      }
    };

    if (selectedInitiative?.roadmapItemId === "0") {
      const newParentRoadmapItem = {
        ...initialRoadmapItemState,
        roadmapId: selectedRoadmap?.id,
        name: selectedInitiative?.name,
      } as RoadmapItemDto;

      createNewRoadmapItem(newParentRoadmapItem, {
        onSuccess: ({
          isSuccessful,
          roadmapItem,
        }: CreateRoadmapItemResponse) => {
          if (isSuccessful && roadmapItem) {
            createTasksCallbackFunction(roadmapItem);
          } else {
            addErrorSnackbar();
          }
        },
        onError: () => addErrorSnackbar(),
      });
    } else if (selectedInitiative) {
      createTasksCallbackFunction(selectedInitiative);
    }
  };

  if (isLoading) {
    return <LoadingOrError loading={isLoading} />;
  }

  const renderCurrentView = () => {
    if (isError) {
      return (
        <LoadingOrError
          error={isError}
          message="There's a problem loading roadmaps data"
          suggestions="Please try again later."
        />
      );
    }
    return (
      <>
        <DialogContent dividers sx={{ p: 0, height: "450px" }}>
          {currentView === "roadmapsView" && (
            <SendToDialogRoadmapsView
              roadmaps={roadmaps.roadmapList!}
              setSnackbars={setSnackbars}
              handleRoadmapClick={handleRoadmapClick}
            />
          )}

          {currentView === "initiativesView" && (
            <SendToDialogInitiativesView
              selectedRoadmap={selectedRoadmap}
              selectedInitiative={selectedInitiative}
              handleInitiativeClick={handleInitiativeClick}
              handleChangeNewInitiativeName={handleChangeNewInitiativeName}
            />
          )}
        </DialogContent>
        {currentView === "initiativesView" && (
          <DialogActions sx={{ justifyContent: "space-between" }}>
            <Button
              variant="text"
              disabled={isBulkCreating || isCreating}
              sx={{ color: theme.palette.common.black }}
              autoFocus
              onClick={handleBackToRoadmaps}
            >
              Back
            </Button>
            {isBulkCreating || isCreating ? (
              <CircularProgress
                sx={{
                  marginRight: theme.spacing(2),
                }}
                size={24}
                color="primary"
                data-testid="bulk-creating-spinner"
              />
            ) : (
              <Button
                disabled={!selectedInitiative?.name?.trim()}
                onClick={handleSubmitNewRoadmapItemFromAssessments}
              >
                Add
              </Button>
            )}
          </DialogActions>
        )}
      </>
    );
  };

  return (
    <>
      <Dialog
        open={isOpenSendToDialog}
        onClose={() => {
          setIsOpenSendToDialog(false);
        }}
        fullWidth
        maxWidth={"sm"}
      >
        <DialogTitle>
          <Typography variant={"body1"}>Add To Roadmap Initiative</Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          disabled={isBulkCreating}
          onClick={() => setIsOpenSendToDialog(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        {renderCurrentView()}
      </Dialog>
      <SnackbarGroup snackbars={snackbars} setSnackbars={setSnackbars} />
    </>
  );
};

export default SendToDialog;
